import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const galleryImages = [
  {
    src: "../images/build1.png",
    alt: "Exterior View",
    label: "Architectural Excellence",
  },
  {
    src: "../images/build2.png",
    alt: "Building Facade",
    label: "Premium Facade",
  },
  {
    src: "../images/build3.png",
    alt: "Swimming Pool",
    label: "Infinity Pool",
  },
  {
    src: "../images/build4.png",
    alt: "Lifestyle Amenity",
    label: "Active Living",
  }
];

export default function ImageGallery() {
  const [lightbox, setLightbox] = useState(null);

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const prev = () => setLightbox((i) => (i > 0 ? i - 1 : galleryImages.length - 1));
  const next = () => setLightbox((i) => (i < galleryImages.length - 1 ? i + 1 : 0));

  return (
    <section
      id="gallery"
      data-testid="image-gallery-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="overline mb-4">Gallery</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">
            Moments between
            <br />
            <span className="italic text-[#C5A059]">the miles</span>.
          </h2>
          <div className="gold-line mx-auto mt-6" />
        </div>

        {/* Horizontal Scrolling Gallery */}
        <div className="overflow-x-auto gallery-scroll pb-4 -mx-4 px-4">
          <div className="flex gap-5 min-w-max">
            {galleryImages.map((img, i) => (
              <button
                key={i}
                data-testid={`gallery-image-${i}`}
                onClick={() => openLightbox(i)}
                className="relative group cursor-pointer flex-shrink-0 w-[300px] md:w-[380px] img-zoom"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-[240px] md:h-[300px] object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500 flex items-end">
                  <div className="p-5 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <span className="text-white text-sm uppercase tracking-[0.15em]">
                      {img.label}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Scroll hint */}
        <div className="flex justify-center mt-6">
          <p className="text-xs text-[#5C5C59] uppercase tracking-[0.2em]">
            Scroll to explore &rarr;
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            data-testid="lightbox-close"
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors z-10"
          >
            <X size={32} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors z-10"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors z-10"
          >
            <ChevronRight size={40} />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-5xl max-h-[85vh] w-full mx-4">
            <img
              src={galleryImages[lightbox].src}
              alt={galleryImages[lightbox].alt}
              className="w-full h-full object-contain"
            />
            <p className="text-center text-white/60 text-sm mt-4 uppercase tracking-[0.15em]">
              {galleryImages[lightbox].label}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
